import {
  Center,
  Flex,
  Text,
  Paper,
  Stack,
  TextInput,
  PasswordInput,
  Alert,
  Button,
  Anchor,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useState, Suspense } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { nprogress } from "@mantine/nprogress";
import { loginSchema } from "../../schemas/auth.schema";
import { Navigate, useSearchParams, Link } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { AxiosError } from "axios";
import { useSessionStore } from "../../store/session.store";
import { routePaths } from "../../constants/paths";

function LoginForm() {
  let [searchParams] = useSearchParams();
  let callbackUrl = searchParams.get("callbackUrl");
  const { sessionExpiryDate } = useSessionStore((state) => state);

  const [visible, { toggle }] = useDisclosure(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { loginUser } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = (values: Yup.InferType<typeof loginSchema>) => {
    setError("");
    setLoading(true);
    nprogress.start();

    loginUser({
      values: {
        email: values.email,
        password: values.password,
      },
      callbackUrl: callbackUrl || undefined,
    })
      .then((res) => {
        if (res !== undefined) {
          if (res instanceof AxiosError) {
            setError(res?.response?.statusText || "Unauthorized!");
            nprogress.complete();
          } else {
            setError("Unauthorized!");
            nprogress.complete();
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (sessionExpiryDate && new Date(sessionExpiryDate) > new Date()) {
    return <Navigate to={routePaths.dashboard} />;
  }

  return (
    <Center mih={"100vh"} h={"100%"}>
      <Flex
        w={{ base: "100%", md: "30%" }}
        direction={"column"}
        align={"center"}
        justify={"center"}
        mx={10}
      >
        <Paper w={"100%"} radius="lg" p="xl" withBorder>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack>
              <Text c={"blue"} fw={800} ta={"center"} size="xl">
                Sign in to your account
              </Text>
              <TextInput
                label="Email"
                type="email"
                placeholder="Your email address"
                {...register("email")}
                error={errors.email?.message}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                visible={visible}
                onVisibilityChange={toggle}
                {...register("password")}
                error={errors.password?.message}
              />
              {error ? (
                <Alert
                  variant="light"
                  color="red"
                  title={error}
                  icon={<IconAlertTriangle />}
                />
              ) : null}
              <Button type="submit" radius="sm" loading={loading}>
                Submit
              </Button>
              <Text ta="center" size="sm">
                Don't have an account?{" "}
                <Anchor component={Link} to={routePaths.signup}>
                  Sign up
                </Anchor>
              </Text>
            </Stack>
          </form>
        </Paper>
      </Flex>
    </Center>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginForm />
    </Suspense>
  );
}
